export type MagentoSku = string

export interface MagentoEntityUrl {
  id: number
  redirectCode: number
  relative_url: string
  sku: string
  type: MagentoUrlRewriteEntityTypeEnum
}

export enum MagentoUrlRewriteEntityTypeEnum {
  CATEGORY = 'CATEGORY',
  CMS_PAGE = 'CMS_PAGE',
  PRODUCT = 'PRODUCT'
}

export interface MagentoProductAttributeSortInput {
  [key: string]: MagentoSortEnum | string
}

export type MagentoSortEnum = 'ASC' | 'DESC'

export interface MagentoProductAttributeFilterInput {
  [key: string]: MagentoFilterEqualTypeInput
}

export interface MagentoFilterEqualTypeInput {
  eq: string
  in: string[]
}

export interface MagentoVisitureProductsPayload {
  visitureProducts: MagentoProducts
}

export interface MagentoProducts {
  aggregations: MagentoAggregation[]
  filters: MagentoLayerFilter[] // @deprecated(reason : "Use aggregations instead")
  items: MagentoProductInterface[]
  page_info: MagentoSearchResultPageInfo
  sort_fields: MagentoSortFields
  total_count: number
}

export interface MagentoAggregation {
  attribute_code: string
  count: number
  label: string
  options: MagentoAggregationOption[]
}

export interface MagentoAggregationOption {
  count: number
  label: string
  value: string
}

export interface MagentoLayerFilter {
  filter_items: MagentoLayerFilterItemInterface[] // @deprecated(reason : "Use Aggregation.options instead.")
  filter_items_count: number // @deprecated(reason : "Use Aggregation.count instead.")
  name: string // @deprecated(reason : "Use Aggregation.label instead.")
  request_var: string // @deprecated(reason : "Use Aggregation.attribute_code instead.")
}

export interface MagentoLayerFilterItemInterface {
  items_count: number // @deprecated(reason : "Use AggregationOption.count instead.")
  label: string // @deprecated(reason : "Use AggregationOption.label instead.")
  value_string: string // @deprecated(reason : "Use AggregationOption.value instead.")
}

export interface MagentoSearchResultPageInfo {
  current_page: number
  page_size: number
  total_pages: number
}

export interface MagentoSortFields {
  default: string
  options: MagentoSortField[]
}

export interface MagentoSortField {
  label: string
  value: string
}

export interface MagentoProductInterface {
  configurable_options: MagentoConfigurableProductOptions[]
  description: MagentoComplexTextValue
  giftcard_amounts: MagentoGiftCardAmounts[]
  canonical_url?: string
  url_rewrites?: Array<{
    url: string
  }>
  id: number
  image: MagentoProductImage
  small_image: MagentoProductImage
  thumbnail: MagentoProductImage
  media_gallery_entries: MagentoMediaGalleryEntry[] // @deprecated(reason : "Use product's `media_gallery` instead")
  name: string
  new_from_date: string
  new_to_date: string
  meta_description: string
  meta_keyword: string
  meta_title: string
  only_x_left_in_stock: number | undefined
  price: MagentoProductPrices // @deprecated(reason : "Use price_range for product price information.")
  price_range: MagentoPriceRange
  product_links: MagentoProductLinksInterface[]
  short_description: any
  sku: MagentoSku //
  status: string
  url_key: string
  pwa_layout?: string
  review_summary?: MagentoReviewSummary
  variants: MagentoConfigurableVariant[]
  categories: MagentoProductInterfaceMagentoCategoryInterface[]
  items?: MagentoGroupedProductItem[]
  visiture_custom_attributes: string
  stock_status: MagentoProductStockStatus
  crosssell_products?: MagentoProductInterface[]
  upsell_products?: MagentoProductInterface[]
  related_products?: MagentoProductInterface[]
  options: MagentoCustomizableOptionInterface[]
  __typename: string
}

export interface MagentoConfigurableProductOptions {
  attribute_code: string
  attribute_id: string
  id: number
  label: string
  position: number
  product_id: number
  use_default: boolean
  values: MagentoConfigurableProductOptionsValues[]
}

export interface MagentoConfigurableProductOptionsValues {
  default_label: string
  label: string
  store_label: string
  use_default_value: boolean
  value_index: number
  visiture_swatch_data: MagentoVisitureSwatchData
}

export interface MagentoVisitureSwatchData {
  type: string
  value: string
}

export interface MagentoComplexTextValue {
  html: string
}

export interface MagentoGiftCardAmounts {
  attribute_id: number
  value: number
  value_id: number
  website_id: number
  website_value: number
}

export interface MagentoProductImage {
  label: string
  url: string
  disabled: boolean
  position: number
}

export interface MagentoMediaGalleryEntry {
  content: MagentoProductMediaGalleryEntriesContent
  disabled: boolean
  file: string
  id: number
  label: string
  media_type: string
  position: number
  types: string[]
  video_content: MagentoProductMediaGalleryEntriesVideoContent
}

export interface MagentoProductMediaGalleryEntriesContent {
  base64_encoded_data: string
  name: string
  type: string
}

export interface MagentoProductMediaGalleryEntriesVideoContent {
  media_type: string
  video_description: string
  video_metadata: string
  video_provider: string
  video_title: string
  video_url: string
}

export interface MagentoProductPrices {
  maximalPrice: MagentoPrice
  minimalPrice: MagentoPrice
  regularPrice: MagentoPrice
}

export interface MagentoPrice {
  amount: MagentoMoney
}

export interface MagentoMoney {
  currency: string
  value: number
}

export interface MagentoProductLinksInterface {
  link_type: string
  linked_product_sku: MagentoSku
  linked_product_type: string
  position: number
  sku: MagentoSku
}

export interface MagentoReviewSummary {
  reviews_count: number
  rating_summary: number
}

export interface MagentoConfigurableVariant {
  attributes: MagentoConfigurableAttributeOption[]
  product: MagentoSimpleProduct
}

export interface MagentoConfigurableAttributeOption {
  code: string
  label: string
  value_index: number
}

export interface MagentoSimpleProduct {
  id: number
  sku: MagentoSku
  status: string
  name: string
  image: MagentoProductImage
  small_image: MagentoProductImage
  thumbnail: MagentoProductImage
  canonical_url?: string
  price_range: MagentoPriceRange
  url_rewrites?: Array<{
    url: string
  }>
  price: MagentoProductPrices
  media_gallery_entries: MagentoMediaGalleryEntry[]
  visiture_custom_attributes: string
  pwa_layout?: string
  only_x_left_in_stock: number | undefined
  is_default_selection?: boolean
  stock_status: MagentoProductStockStatus
  short_description?: MagentoComplexTextValue
  __typename: string
}

export enum MagentoProductStockStatus {
  IN_STOCK = 'IN_STOCK',
  OUT_OF_STOCK = 'OUT_OF_STOCK'
}

export interface MagentoProductInterfaceMagentoCategoryInterface {
  breadcrumbs: MagentoBreadcrumb[] | null
  name: string
  id: number
}

export interface MagentoBreadcrumb {
  category_url_key: string
  category_name: string
}

export interface MagentoGroupedProductItem {
  position: number
  qty: number
  product: {
    name: string
    sku: string
    url_key: string
    price: MagentoProductPrices
    __typename: string
  }
}

export interface MagentoCustomizableOptionInterface {
  dropdown_value?: MagentoCustomizableDropDownValue[]
  multiple_value?: MagentoCustomizableMultipleValue[]
  radio_value?: MagentoCustomizableRadioValue[]
  field_value?: MagentoCustomizableFieldValue
  checkbox_value?: MagentoCustomizableCheckboxValue[]
  area_value?: MagentoCustomizableAreaValue
  option_id: number
  required: boolean
  sort_order: number
  title: string
  __typename?: string
}

export interface MagentoCustomizableDropDownValue {
  option_type_id: number
  price: number
  price_type: MagentoPriceTypeEnum
  sku: string
  sort_order: number
  title: string
}

export interface MagentoCustomizableMultipleValue {
  option_type_id: number
  price: number
  price_type: MagentoPriceTypeEnum
  sku: string
  sort_order: number
  title: string
}

export interface MagentoCustomizableRadioValue {
  option_type_id: number
  price: number
  price_type: MagentoPriceTypeEnum
  sku: string
  sort_order: number
  title: string
}

export interface MagentoCustomizableFieldValue {
  max_characters: number
  price: number
  price_type: MagentoPriceTypeEnum
  sku: MagentoSku
}

export interface MagentoCustomizableCheckboxValue {
  option_type_id: number
  price: number
  price_type: MagentoPriceTypeEnum
  sku: string
  sort_order: number
  title: string
}

export interface MagentoCustomizableAreaValue {
  max_characters: number
  price: number
  price_type: MagentoPriceTypeEnum
  sku: string
}

export enum MagentoPriceTypeEnum {
  DYNAMIC = 'DYNAMIC',
  FIXED = 'FIXED',
  PERCENT = 'PERCENT'
}

export interface MagentoIsEmailAvailableOutput {
  is_email_available: boolean
}

export interface MagentoCategoryCustomAttribute {
  attribute_code: string
  value: string
}

export interface MagentoSearchTerm {
  query_id: number
  display_in_terms: number
  is_active: number
  is_processed: number
  num_results: number
  popularity: number
  query_text: string
  redirect: string | null
  store_id: number
}

export interface MagentoStoreConfig {
  absolute_footer: string
  base_currency_code: string
  base_link_url: string
  base_media_url: string
  base_static_url: string
  base_url: string
  catalog_default_sort_by: string
  category_url_suffix: string
  cms_home_page: string
  cms_no_cookies: string
  cms_no_route: string
  code: string
  copyright: string
  country_options: {
    optional_zip_countries?: MagentoCountryCodeEnum[]
  }
  create_account_confirmation?: boolean
  default_description: string
  default_display_currency_code: string
  default_keywords: string
  default_title: string
  demonotice: number
  front: string
  head_includes: string
  head_shortcut_icon: string
  header_logo_src: string
  id: number
  locale: string
  logo_alt: string
  logo_height: number
  logo_width: number
  no_route: string
  pwa_url?: string
  product_url_suffix: string
  region_options: {
    state_required?: MagentoCountryCodeEnum[]
  }
  secure_base_link_url: string
  secure_base_media_url: string
  secure_base_static_url: string
  secure_base_url: string
  show_cms_breadcrumbs: number
  timezone: string
  title_prefix: string
  title_suffix: string
  website_id: number
  weight_unit: string
  welcome: string
  grid_per_page: number
  grid_per_page_values: string
}

export interface MagentoCustomerPaymentTokens {
  items: MagentoPaymentToken[]
}

export interface MagentoPaymentToken {
  details?: string
  payment_method_code: string
  public_hash: string
  type: MagentoPaymentTokenTypeEnum
}

export enum MagentoPaymentTokenTypeEnum {
  Account = 'account',
  Card = 'card'
}

export enum MagentoCreditCardType {
  Visa = 'VI',
  MasterCard = 'MC',
  Amex = 'AE',
  DinersClub = 'DN',
  Discover = 'DI',
  JCB = 'JCB',
  UnionPay = 'UN',
  Maestro = 'SM',
  ELO = 'ELO',
  MIR = 'MI',
  Hipercard = 'HC',
  Other = 'OT'
}

export interface MagentoCmsPage {
  active: boolean
  id: number
  identifier: string
  title: string
  meta_title: string
  meta_keywords: string
  meta_description: string
  content_heading: string
  content: string
  sort_order: string
  pwa_layout: string
}

export interface MagentoCategoryItem {
  id: number
  name: string
  children: string
  created_at: string
  include_in_menu: boolean
  is_active: boolean
  parent_id?: number
  path: string
  level: number
  position: number
  updated_at: string
  custom_attributes: MagentoCategoryCustomAttribute[]
  pwa_layout?: string
}

export interface MagentoCart {
  applied_coupon: MagentoAppliedCoupon
  applied_gift_cards: any
  available_payment_methods?: MagentoAvailablePaymentMethod[]
  billing_address: MagentoBillingCartAddress
  email: string
  items: MagentoCartItemInterface[]
  prices: MagentoCartPrices
  selected_payment_method?: MagentoSelectedPaymentMethod
  shipping_addresses: MagentoShippingCartAddress[]
  done?: boolean
}

export interface MagentoAppliedCoupon {
  code: string
}

export interface MagentoCartPrices {
  applied_taxes: MagentoCartTaxItem[]
  discount_amount: MagentoMoney
  grand_total: MagentoMoney
  subtotal_excluding_tax: MagentoMoney
  subtotal_including_tax: MagentoMoney
  subtotal_with_discount_excluding_tax: MagentoMoney
}

export interface MagentoSelectedPaymentMethod {
  code: string
  purchase_order_number: string
  title: string
}

export interface MagentoCartAddressInterface {
  customer_address_id?: number
  city: string
  company: string
  country: MagentoCartAddressCountry
  customer_notes: string
  firstname: string
  lastname: string
  postcode: string
  region: MagentoCartAddressRegion
  street: string[]
  telephone: string
}

export interface MagentoCartAddressCountry {
  code: string
  label: string
}

export interface MagentoCartAddressRegion {
  code: string
  label: string
}

export interface MagentoShippingCartAddress extends MagentoCartAddressInterface {
  available_shipping_methods?: MagentoAvailableShippingMethod[]
  cart_items: MagentoCartItemQuantity[]
  city: string
  company: string
  country: MagentoCartAddressCountry
  customer_notes: string
  firstname: string
  lastname: string
  postcode: string
  region: MagentoCartAddressRegion
  selected_shipping_method?: MagentoSelectedShippingMethod
  street: string[]
  telephone: string
}

export interface MagentoBillingCartAddress extends MagentoCartAddressInterface {
  city: string
  company: string
  country: MagentoCartAddressCountry
  customer_notes: string
  firstname: string
  lastname: string
  postcode: string
  region: MagentoCartAddressRegion
  street: string[]
  telephone: string
}

export interface MagentoCartItemQuantity {
  cart_item_id: number
  quantity: number
}

export interface MagentoAvailablePaymentMethod {
  code: string
  title: string
}

export interface MagentoAvailableShippingMethod {
  amount: MagentoMoney
  available: boolean
  base_amount: MagentoMoney
  carrier_code: string
  carrier_title: string
  error_message: string
  method_code: string
  method_title: string
  price_excl_tax: MagentoMoney
  price_incl_tax: MagentoMoney
}

export interface MagentoSelectedCustomizableOption {
  id: number
  label: string
  values: MagentoSelectedCustomizableOptionValue[]
}

export interface MagentoCartItemInterface {
  id: string
  prices: MagentoCartItemPrices
  product: MagentoProductInterface
  quantity: number
  variant_product?: MagentoProductInterface
  customizable_options?: MagentoSelectedCustomizableOption[]
  configurable_options?: MagentoSelectedConfigurableOption[]
}

export interface MagentoCartTaxItem {
  amount: MagentoMoney
  label: string
}

export interface MagentoSelectedConfigurableOption {
  id: number
  option_label: string
  value_id: number
  value_label: string
}

export interface MagentoSelectedShippingMethod {
  amount: MagentoMoney
  base_amount: MagentoMoney
  carrier_code: string
  carrier_title: string
  method_code: string
  method_title: string
}

export interface MagentoCartItemPrices {
  discounts: MagentoDiscount[] | null
  price: MagentoMoney
  row_total: MagentoMoney
  row_total_including_tax: MagentoMoney
  total_item_discount: MagentoMoney | null
}

export interface MagentoDiscount {
  amount: MagentoMoney
  label: string
}

export interface MagentoGetCartGiftMessageOutput {
  items?: MagentoGiftMessageItemOutput[]
  message?: string
  recipient?: string
  sender?: string
}

export interface MagentoGiftMessageItemOutput {
  item_id: number
  message?: string
  recipient?: string
  sender?: string
}

export interface MagentoCustomerOutput {
  customer: {
    email: String
    firstname: String
    lastname: String
  }
}

export interface MagentoSelectedCustomizableOptionValue {
  id: number
  label: string
  value: string
  price: MagentoCartItemSelectedOptionValuePrice
}

export interface MagentoCartItemSelectedOptionValuePrice {
  type: MagentoPriceTypeEnum
  units: string
  value: number
}

export interface MagentoPriceRange {
  maximum_price: MagentoProductPrice
  minimum_price: MagentoProductPrice
}

export interface MagentoProductPrice {
  discount: MagentoProductDiscount
  final_price: MagentoMoney
  fixed_product_taxes: MagentoFixedProductTax[]
  regular_price: MagentoMoney
}

export interface MagentoProductDiscount {
  amount_off: number
  percent_off: number
}

export interface MagentoFixedProductTax {
  amount: MagentoMoney
  label: string
}

export enum MagentoCountryCodeEnum {
  EMPTY = '',
  // #Andorra
  AD = 'AD',
  // United Arab Emirates
  AE = 'AE',
  // Afghanistan
  AF = 'AF',
  // Antigua & Barbuda
  AG = 'AG',
  // Anguilla
  AI = 'AI',
  // Albania
  AL = 'AL',
  // Armenia
  AM = 'AM',
  // Netherlands Antilles
  AN = 'AN',
  // Angola
  AO = 'AO',
  // Antarctica
  AQ = 'AQ',
  // Argentina
  AR = 'AR',
  // American Samoa
  AS = 'AS',
  // Austria
  AT = 'AT',
  // Australia
  AU = 'AU',
  // Aruba
  AW = 'AW',
  // Åland Islands
  AX = 'AX',
  // Azerbaijan
  AZ = 'AZ',
  // Bosnia & Herzegovina
  BA = 'BA',
  // Barbados
  BB = 'BB',
  // Bangladesh
  BD = 'BD',
  // Belgium
  BE = 'BE',
  // Burkina Faso
  BF = 'BF',
  // Bulgaria
  BG = 'BG',
  // Bahrain
  BH = 'BH',
  // Burundi
  BI = 'BI',
  // Benin
  BJ = 'BJ',
  // St. Barthélemy
  BL = 'BL',
  // Bermuda
  BM = 'BM',
  // Brunei
  BN = 'BN',
  // Bolivia
  BO = 'BO',
  // Brazil
  BR = 'BR',
  // Bahamas
  BS = 'BS',
  // Bhutan
  BT = 'BT',
  // Bouvet Island
  BV = 'BV',
  // Botswana
  BW = 'BW',
  // Belarus
  BY = 'BY',
  // Belize
  BZ = 'BZ',
  // Canada
  CA = 'CA',
  // Cocos (Keeling) Islands
  CC = 'CC',
  // Congo-Kinshasa
  CD = 'CD',
  // Central African Republic
  CF = 'CF',
  // Congo-Brazzaville
  CG = 'CG',
  // Switzerland
  CH = 'CH',
  // Côte d’Ivoire
  CI = 'CI',
  // Cook Islands
  CK = 'CK',
  // Chile
  CL = 'CL',
  // Cameroon
  CM = 'CM',
  // China
  CN = 'CN',
  // Colombia
  CO = 'CO',
  // Costa Rica
  CR = 'CR',
  // Cuba
  CU = 'CU',
  // Cape Verde
  CV = 'CV',
  // Christmas Island
  CX = 'CX',
  // Cyprus
  CY = 'CY',
  // Czech Republic
  CZ = 'CZ',
  // Germany
  DE = 'DE',
  // Djibouti
  DJ = 'DJ',
  // Denmark
  DK = 'DK',
  // Dominica
  DM = 'DM',
  // Dominican Republic
  DO = 'DO',
  // Algeria
  DZ = 'DZ',
  // Ecuador
  EC = 'EC',
  // Estonia
  EE = 'EE',
  // Egypt
  EG = 'EG',
  // Western Sahara
  EH = 'EH',
  // Eritrea
  ER = 'ER',
  // Spain
  ES = 'ES',
  // Ethiopia
  ET = 'ET',
  // Finland
  FI = 'FI',
  // Fiji
  FJ = 'FJ',
  // Falkland Islands
  FK = 'FK',
  // Micronesia
  FM = 'FM',
  // Faroe Islands
  FO = 'FO',
  // France
  FR = 'FR',
  // Gabon
  GA = 'GA',
  // United Kingdom
  GB = 'GB',
  // Grenada
  GD = 'GD',
  // Georgia
  GE = 'GE',
  // French Guiana
  GF = 'GF',
  // Guernsey
  GG = 'GG',
  // Ghana
  GH = 'GH',
  // Gibraltar
  GI = 'GI',
  // Greenland
  GL = 'GL',
  // Gambia
  GM = 'GM',
  // Guinea
  GN = 'GN',
  // Guadeloupe
  GP = 'GP',
  // Equatorial Guinea
  GQ = 'GQ',
  // Greece
  GR = 'GR',
  // South Georgia & South Sandwich Islands
  GS = 'GS',
  // Guatemala
  GT = 'GT',
  // Guam
  GU = 'GU',
  // Guinea-Bissau
  GW = 'GW',
  // Guyana
  GY = 'GY',
  // Hong Kong SAR China
  HK = 'HK',
  // Heard &amp; McDonald Islands
  HM = 'HM',
  // Honduras
  HN = 'HN',
  // Croatia
  HR = 'HR',
  // Haiti
  HT = 'HT',
  // Hungary
  HU = 'HU',
  // Indonesia
  ID = 'ID',
  // Ireland
  IE = 'IE',
  // Israel
  IL = 'IL',
  // Isle of Man
  IM = 'IM',
  // India
  IN = 'IN',
  // British Indian Ocean Territory
  IO = 'IO',
  // Iraq
  IQ = 'IQ',
  // Iran
  IR = 'IR',
  // Iceland
  IS = 'IS',
  // Italy
  IT = 'IT',
  // Jersey
  JE = 'JE',
  // Jamaica
  JM = 'JM',
  // Jordan
  JO = 'JO',
  // Japan
  JP = 'JP',
  // Kenya
  KE = 'KE',
  // Kyrgyzstan
  KG = 'KG',
  // Cambodia
  KH = 'KH',
  // Kiribati
  KI = 'KI',
  // Comoros
  KM = 'KM',
  // St. Kitts & Nevis
  KN = 'KN',
  // North Korea
  KP = 'KP',
  // South Korea
  KR = 'KR',
  // Kuwait
  KW = 'KW',
  // Cayman Islands
  KY = 'KY',
  // Kazakhstan
  KZ = 'KZ',
  // Laos
  LA = 'LA',
  // Lebanon
  LB = 'LB',
  // St. Lucia
  LC = 'LC',
  // Liechtenstein
  LI = 'LI',
  // Sri Lanka
  LK = 'LK',
  // Liberia
  LR = 'LR',
  // Lesotho
  LS = 'LS',
  // Lithuania
  LT = 'LT',
  // Luxembourg
  LU = 'LU',
  // Latvia
  LV = 'LV',
  // Libya
  LY = 'LY',
  // Morocco
  MA = 'MA',
  // Monaco
  MC = 'MC',
  // Moldova
  MD = 'MD',
  // Montenegro
  ME = 'ME',
  // St. Martin
  MF = 'MF',
  // Madagascar
  MG = 'MG',
  // Marshall Islands
  MH = 'MH',
  // Macedonia
  MK = 'MK',
  // Mali
  ML = 'ML',
  // Myanmar (Burma)
  MM = 'MM',
  // Mongolia
  MN = 'MN',
  // Macau SAR China
  MO = 'MO',
  // Northern Mariana Islands
  MP = 'MP',
  // Martinique
  MQ = 'MQ',
  // Mauritania
  MR = 'MR',
  // Montserrat
  MS = 'MS',
  // Malta
  MT = 'MT',
  // Mauritius
  MU = 'MU',
  // Maldives
  MV = 'MV',
  // Malawi
  MW = 'MW',
  // Mexico
  MX = 'MX',
  // Malaysia
  MY = 'MY',
  // Mozambique
  MZ = 'MZ',
  // Namibia
  NA = 'NA',
  // New Caledonia
  NC = 'NC',
  // Niger
  NE = 'NE',
  // Norfolk Island
  NF = 'NF',
  // Nigeria
  NG = 'NG',
  // Nicaragua
  NI = 'NI',
  // Netherlands
  NL = 'NL',
  // Norway
  NO = 'NO',
  // Nepal
  NP = 'NP',
  // Nauru
  NR = 'NR',
  // Niue
  NU = 'NU',
  // New Zealand
  NZ = 'NZ',
  // Oman
  OM = 'OM',
  // Panama
  PA = 'PA',
  // Peru
  PE = 'PE',
  // French Polynesia
  PF = 'PF',
  // Papua New Guinea
  PG = 'PG',
  // Philippines
  PH = 'PH',
  // Pakistan
  PK = 'PK',
  // Poland
  PL = 'PL',
  // St. Pierre & Miquelon
  PM = 'PM',
  // Pitcairn Islands
  PN = 'PN',
  // Palestinian Territories
  PS = 'PS',
  // Portugal
  PT = 'PT',
  // Palau
  PW = 'PW',
  // Paraguay
  PY = 'PY',
  // Qatar
  QA = 'QA',
  // Réunion
  RE = 'RE',
  // Romania
  RO = 'RO',
  // Serbia
  RS = 'RS',
  // Russia
  RU = 'RU',
  // Rwanda
  RW = 'RW',
  // Saudi Arabia
  SA = 'SA',
  // Solomon Islands
  SB = 'SB',
  // Seychelles
  SC = 'SC',
  // Sudan
  SD = 'SD',
  // Sweden
  SE = 'SE',
  // Singapore
  SG = 'SG',
  // St. Helena
  SH = 'SH',
  // Slovenia
  SI = 'SI',
  // Svalbard & Jan Mayen
  SJ = 'SJ',
  // Slovakia
  SK = 'SK',
  // Sierra Leone
  SL = 'SL',
  // San Marino
  SM = 'SM',
  // Senegal
  SN = 'SN',
  // Somalia
  SO = 'SO',
  // Suriname
  SR = 'SR',
  // São Tomé & Príncipe
  ST = 'ST',
  // El Salvador
  SV = 'SV',
  // Syria
  SY = 'SY',
  // Swaziland
  SZ = 'SZ',
  // Turks & Caicos Islands
  TC = 'TC',
  // Chad
  TD = 'TD',
  // French Southern Territories
  TF = 'TF',
  // Togo
  TG = 'TG',
  // Thailand
  TH = 'TH',
  // Tajikistan
  TJ = 'TJ',
  // Tokelau
  TK = 'TK',
  // Timor-Leste
  TL = 'TL',
  // Turkmenistan
  TM = 'TM',
  // Tunisia
  TN = 'TN',
  // Tonga
  TO = 'TO',
  // Turkey
  TR = 'TR',
  // Trinidad & Tobago
  TT = 'TT',
  // Tuvalu
  TV = 'TV',
  // Taiwan
  TW = 'TW',
  // Tanzania
  TZ = 'TZ',
  // Ukraine
  UA = 'UA',
  // Uganda
  UG = 'UG',
  // U.S. Outlying Islands
  UM = 'UM',
  // United States
  US = 'US',
  // Uruguay
  UY = 'UY',
  // Uzbekistan
  UZ = 'UZ',
  // Vatican City
  VA = 'VA',
  // St. Vincent & Grenadines
  VC = 'VC',
  // Venezuela
  VE = 'VE',
  // British Virgin Islands
  VG = 'VG',
  // U.S. Virgin Islands
  VI = 'VI',
  // Vietnam
  VN = 'VN',
  // Vanuatu
  VU = 'VU',
  // Wallis & Futuna
  WF = 'WF',
  // Samoa
  WS = 'WS',
  // Yemen
  YE = 'YE',
  // Mayotte
  YT = 'YT',
  // South Africa
  ZA = 'ZA',
  // Zambia
  ZM = 'ZM',
  // Zimbabwe
  ZW = 'ZW'
}
